import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { User } from "lucide-react";
import { Media, MediaType } from "src/interfaces/Media";

interface MediaPickerCardProps {
  item: Media;
  selected: boolean;
  onClick: () => void;
  corporateId: string | number;
}

const getFallbackImage = (type: MediaType): string => {
  switch (type) {
    case MediaType.COVER:
      return "library-cover.jpg";
    case MediaType.VIDEO:
      return "library-video.jpg";
    case MediaType.LEARNING_RESOURCE:
      return "library-learning-resource.jpg";
    case MediaType.DIDACTICAL_MATERIAL:
      return "library-didactical-material.jpg";
    case MediaType.LEGAL_DOCUMENTATION:
      return "library-legal-documentation.jpg";
    case MediaType.OTHER:
    default:
      return "library-other.jpg";
  }
};

const MediaPickerCard: React.FC<MediaPickerCardProps> = ({
  item,
  selected,
  onClick,
  corporateId,
}) => {
  const theme = useTheme();
  const name = item.metadata?.name || `Media #${item.id}`;
  const author = item.createdBy || "";
  const fallbackUrl = `${process.env.REACT_APP_THEME_ADMIN}/${corporateId}/images/${getFallbackImage(
    item.metadata?.mediaType,
  )}`;
  const previewUrl =
    item.metadata?.mediaType === MediaType.COVER && item.url?.public_url
      ? item.url.public_url
      : fallbackUrl;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: "12px",
        border: `2px solid ${
          selected ? theme.palette.primary.main : "transparent"
        }`,
        backgroundColor: theme.palette.background.paper,
        transition:
          "border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: selected
            ? theme.palette.primary.main
            : theme.palette.divider,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          backgroundImage: `url(${previewUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: theme.palette.action.disabledBackground,
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          padding: "12px 14px 14px",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {name}
        </Typography>

        {(author || item.id) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: theme.palette.text.primary,
            }}
          >
            <User size={14} />
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[item.id, author].filter(Boolean).join(", ")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MediaPickerCard;
